<template>
  <div
    class="offline-page onboarding-container"
    :style="{backgroundColor: !isModal ? '#fcfcfc' : 'transparent', backdropFilter: !isModal ? '' : 'blur(10px)'}"
  >
    <div class="onboarding-header" v-if="!isModal">
      <img :src="config.logo_url_light || config.logo_url" :alt="currentEcosystem.name" class="onboarding-logo">
    </div>
    <div class="onboarding-body scrollable" :style="{justifyContent: `${justifyContent}px`, paddingTop: `${onboardingPaddingTop}px`}">
      <div class="onboarding-card container">
        <div class="onboarding-card-left"
             :style="{backgroundImage: `url(${coverImage})`}">
        </div>
        <div class="onboarding-card-right">
          <div class="onboarding-content">
            <div class="onboarding-component-container" style="display: inline-block;">
              <h1 class="h1" v-html="title"></h1>
              <p style="margin-top: 10px;" v-html="subtitle"></p>
              <button class="button button--rounded" @click.prevent="contactAdministrator">{{ ctaLabel }}</button>
            </div>
          </div>
          <template
            v-if="showLanguageSelection">
            <div class="language-selection language-selection--english"
                 :class="{'language-selected': activeLocale === 'en'}"
                 @click="setActiveLocaleLocally('en')">
              English
            </div>
            <div class="language-selection language-selection--dutch"
                 :class="{'language-selected': activeLocale === 'nl'}"
                 @click="setActiveLocaleLocally('nl')">
              Nederlands
            </div>
          </template>
        </div>
        <button class="onboarding-close-button" @click="close" v-if="isCloseable && isModal">×</button>
      </div>
    </div>
    <div class="onboarding-footer" v-if="!isModal">
      <span>
        {{ $t('onboarding_hosted_by') }}
        <a href="https://www.datascouts.eu" target="_blank" style="color: white">DATASCOUTS</a>
        - <a href="https://logo.dev" class="text-white">Logos provided by Logo.dev</a>
      </span>
    </div>
  </div>
</template>

<script>
  import Avatar from '../components/Avatar/Avatar.vue'
  import { MUTATION_TYPES as LOCALIZATION_MUTATION_TYPES } from '../store/modules/localization.js'
  import OnboardingMixin from '../util/OnboardingMixin.js'
  import { defineComponent } from 'vue'

  export default defineComponent({
    props: {
      type: String,
      isCloseable: {
        type: Boolean,
        default: true,
      },
      isModal: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['close'],
    data () {
      return {
        onboardingPaddingTop: 0,
        justifyContent: 'flex-start',
      }
    },

    methods: {
      contactAdministrator () {
        if (this.$store.getters.isEcHealth) {
          window.open('https://ec-health.nz.datascouts.eu', '_blank')
          return
        }

        if (this.$store.getters.isEcAerospace) {
          window.open('https://ec-aerospace.nz.datascouts.eu', '_blank')
          return
        }

        if (this.$store.getters.isEcCleantech) {
          window.open('https://ec-cleantech.nz.datascouts.eu', '_blank')
          return
        }

        if (this.$store.getters.isOpenCreativesZuidwest) {
          window.open('https://opencreatives.org/communities/90', '_blank')
          return
        }

        if (this.$store.getters.isOpenCreativesWaasland) {
          window.open('https://opencreatives.org/communities/92', '_blank')
          return
        }

        if (this.$store.getters.isOpenCreativesLeuven) {
          window.open('https://opencreatives.org/communities/89', '_blank')
          return
        }

        window.open('mailto:' + this.emailAddress)
      },
      close () {
        this.$emit('close')
      },
      setActiveLocaleLocally (locale) {
        // !! we don't use "dispatch -> action_types -> update_active_locale" here because the user is not logged in
        this.$store.commit(LOCALIZATION_MUTATION_TYPES.SET_ACTIVE_LOCALE, { locale, i18n: this.$i18n })
      },
      calculateCardHeight () {
        this.onboardingPaddingTop = 40

        if (window.innerHeight >= 992) {
          this.justifyContent = 'center'
        }

        if (window.innerHeight >= 600) {
          return
        }

        this.onboardingPaddingTop = 60
      },
    },
    computed: {
      ctaLabel () {
        if (this.$store.getters.isEcHealth) {
          return 'Visit the health ecosystem'
        }

        if (this.$store.getters.isEcAerospace || this.$store.getters.isEcCleantech) {
          return 'Visit the aerospace ecosystem'
        }

        if (this.$store.getters.isOpenCreativesZuidwest) {
          return 'Bezoek de Open Creatives Zuidwest Community'
        }

        if (this.$store.getters.isOpenCreativesWaasland) {
          return 'Bezoek de Open Creatives Waasland Community'
        }

        if (this.$store.getters.isOpenCreativesLeuven) {
          return 'Bezoek de Open Creatives Leuven Community'
        }

        return 'Contact administrator'
      },
      title () {
        if (this.$store.getters.isEcHealth || this.$store.getters.isEcAerospace || this.$store.getters.isEcCleantech) {
          return 'This platform has been moved'
        }

        if (this.$store.getters.isOpenCreativesWaasland || this.$store.getters.isOpenCreativesZuidwest || this.$store.getters.isOpenCreativesLeuven) {
          return 'Wij zijn verhuisd!'
        }

        return this.ecosystemName + ' is temporarily unavailable'
      },
      subtitle () {
        if (this.$store.getters.isEcHealth || this.$store.getters.isEcAerospace || this.$store.getters.isEcCleantech) {
          return 'This site has been moved to a new address. Click the button below to continue — you\'ll be back on your regular URL again soon.'
        }

        if (this.$store.getters.isOpenCreativesWaasland || this.$store.getters.isOpenCreativesZuidwest || this.$store.getters.isOpenCreativesLeuven) {
          return 'Bezoek onze community op het algemene Open Creatives Platform!'
        }

        return 'You are not able to login at the moment. If this problem persists, please&nbsp;contact&nbsp;your&nbsp;admin for further assistance.'
      },
      emailAddress () {
        return this.$store.state.config.email ?? 'support@datascouts.eu'
      },
      ecosystemName () {
        return this.$store.getters.currentEcosystem.displayName || this.$store.getters.currentEcosystem.name
      },
      coverImage () {
        return this.config.onboarding_image_url || this.config.coverSrc || '/images/covers/skyline.jpeg'
      },
      showLanguageSelection () {
        return this.config.languages && this.config.languages.includes('nl') && this.currentStep === 1 && this.STEPS.length > 1 && this.type !== 'packages-sign-up'
      },
      activeLocale () {
        return this.$store.getters.activeLocale
      },
      coverSrc () {
        return this.config.coverSrc || '/images/covers/skyline.jpeg'
      },
      config () {
        return this.$store.state.config
      },
      isPublic () {
        return this.$store.getters.isPublic
      },
    },
    mixins: [OnboardingMixin],
    components: {
      Avatar
    },
  })
</script>

<style lang="scss" scoped>
  @import "../../scss/variables.scss";

  .offline-page {
    .onboarding-card-right {
      padding: 40px 30px;
    }

    @media only screen and (max-width: $screen-xl) {
      .onboarding-body {
        margin-top: 100px;
      }
    }
  }
</style>
